import { prefersReducedMotion } from '@/scripts/core/motion'

// A shade CTA rendered by snippets/shade-select.liquid with no shade_select_url
// scrolls to the shade grid on the same page: the PDP shade panel or the
// homepage Shade Tile. The PDP hides its grid once a shade is chosen; the link
// asks it to open again (pdp-shade-panel handles shade-grid:open) before it
// scrolls. With no grid at all the link follows its fallback href.
// #find-my-shade links and URLs, which opened the retired Findation drawer,
// land on the grid too.
const FIND_MY_SHADE_HASH = '#find-my-shade'
const LINK_SELECTOR = `a[data-shade-scroll], a[href="${FIND_MY_SHADE_HASH}"]`
const GRID_SELECTOR = '[data-shade-grid]'

const isVisible = (grid: HTMLElement): boolean =>
	!grid.hidden && grid.getClientRects().length > 0

function visibleGrid(): HTMLElement | undefined {
	return Array.from(document.querySelectorAll<HTMLElement>(GRID_SELECTOR)).find(
		isVisible
	)
}

function openGrid(): HTMLElement | undefined {
	const visible = visibleGrid()
	if (visible) return visible
	const hidden = document.querySelector<HTMLElement>(`${GRID_SELECTOR}[hidden]`)
	hidden?.dispatchEvent(new CustomEvent('shade-grid:open'))
	return hidden && isVisible(hidden) ? hidden : undefined
}

// The PDP grid is a snap stage whose scroll margin already allows for the
// header and the iOS status-bar inset, so it lands where snapping rests it.
// Elsewhere the grid starts below the sticky header.
function gridTop(grid: HTMLElement): number {
	const style = getComputedStyle(grid)
	const top = grid.getBoundingClientRect().top + window.scrollY
	const snapping =
		style.scrollSnapAlign !== 'none' &&
		getComputedStyle(document.documentElement).scrollSnapType !== 'none'
	const offset = snapping
		? parseFloat(style.scrollMarginTop) || 0
		: Math.max(
				0,
				document.querySelector('.section-header')?.getBoundingClientRect()
					.bottom ?? 0
			)
	return Math.max(0, Math.round(top - offset))
}

function handleClick(event: MouseEvent): void {
	if (
		event.defaultPrevented ||
		event.button !== 0 ||
		event.metaKey ||
		event.ctrlKey ||
		event.shiftKey ||
		event.altKey
	)
		return
	const link =
		event.target instanceof Element
			? event.target.closest<HTMLAnchorElement>(LINK_SELECTOR)
			: null
	if (!link) return
	const grid = openGrid()
	if (!grid) return
	event.preventDefault()
	scrollToGrid(grid)
	// Keyboard activation (detail 0) carries focus to the first shade. A pointer
	// click does not: a focus ring on Shade 01 would read as a preselected shade.
	if (event.detail === 0)
		grid.querySelector<HTMLElement>('a[href], button')?.focus({ preventScroll: true })
}

function scrollToGrid(grid: HTMLElement): void {
	window.scrollTo({
		top: gridTop(grid),
		behavior: prefersReducedMotion() ? 'instant' : 'smooth',
	})
}

export function startShadeScroll(): void {
	document.addEventListener('click', handleClick)
	if (window.location.hash !== FIND_MY_SHADE_HASH) return
	const landOnGrid = (): void => {
		const grid = visibleGrid()
		if (grid) scrollToGrid(grid)
	}
	if (document.readyState === 'complete') landOnGrid()
	else window.addEventListener('load', landOnGrid, { once: true })
}
